import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Send, FileText, FileDown, Trash2, ChevronDown, Hospital, Pencil, Share2, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const selectCls = inputCls + " bg-white";

const RISK_OPTS = ["Low", "Medium", "High", "Critical"];
const YN_OPTS = ["Yes", "No"];
const SUPPORT_OPTS = [
  { v: "preferred", label: "Preferred" },
  { v: "essential", label: "Essential" },
  { v: "both", label: "Preferred + Essential" },
  { v: "none", label: "Not required" },
];

const EQUIPMENT_SECTIONS = [
  { key: "qty_emergency", title: "Emergency equipment", items: ["ZOLL X Series Monitor/Defibrillator", "AED (Automated External Defibrillator)", "Oxygen Therapy Equipment", "Suction Unit (Portable)", "Airway Management Equipment", "Trauma Bags (Advanced)", "Resuscitation Equipment", "Spinal Immobilisation Equipment", "Burns Kit", "Obstetrics Kit", "Evacuation Chair"] },
  { key: "qty_medical_centre", title: "Medical centre equipment", items: ["Treatment Trolleys", "Clinical Consumables", "Vital Signs Monitoring Equipment", "Nebuliser", "Patient Examination Couch", "Wheelchair", "Folding Chairs (Patients)", "Cots / Camp Beds", "Privacy Screens", "Sharps Bins", "Welfare Supplies (Hot/Cold)", "Hand Hygiene Stations"] },
  { key: "qty_ambulance", title: "Ambulance equipment", items: ["Front-line Ambulance Equipment", "Oxygen Cylinders (Full)", "Advanced Airway Equipment", "IV Fluids & Administration Sets", "Trauma & Response Bags", "Scoop Stretcher", "Spinal Board & Head Blocks", "Patient Monitoring Equipment", "Defibrillator (Back-up)", "Suction Unit", "Spare Consumables"] },
  { key: "qty_communications", title: "Communications equipment", items: ["Event Radios (Motorola)", "Base Radio (Medical Centre)", "Starlink Satellite Internet System", "Mobile Phones (Back-up)", "GPS Tracking Devices", "Portable Power Banks"] },
];

const SUPPORT_ITEMS = [
  ["power", "16-amp power supply"], ["barriers", "Crowd barriers around medical centre"],
  ["water", "Drinking water supply"], ["control", "Direct Event Control contact"],
  ["access", "Emergency vehicle access route"], ["location", "Suitable medical centre location"],
  ["portaloo", "Portaloo facilities for patients"], ["waste", "Waste disposal arrangements"],
  ["welfare", "Staff welfare facilities"], ["food", "Staff food tokens"],
];

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <input type={type} className={inputCls} value={value || ""} placeholder={placeholder || ""}
        data-testid={testid} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function SelField({ label, value, onChange, options, testid, allowBlank = true }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <select className={selectCls} value={value || ""} data-testid={testid} onChange={(e) => onChange(e.target.value)}>
        {allowBlank && <option value="">—</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function CheckField({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer">
      <input type="checkbox" className="h-4 w-4 accent-[#005EB8]" checked={!!checked}
        data-testid={testid} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

function Section({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between px-4 sm:px-5 py-4 text-left">
        <span className="font-heading font-bold text-[#0F2547] text-sm sm:text-base">{title}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function AnnotationEditor({ planId, annotations, onChanged }) {
  const [imgSrc, setImgSrc] = useState("");
  const [imgNat, setImgNat] = useState({ w: 0, h: 0 });
  const [boxes, setBoxes] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    const im = new Image();
    im.onload = () => setImgNat({ w: im.naturalWidth, h: im.naturalHeight });
    im.src = url;
    setImgSrc(url);
    setBoxes([]);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const addBox = (e) => {
    if (!areaRef.current || e.target.closest("[data-box]")) return;
    const r = areaRef.current.getBoundingClientRect();
    setBoxes((b) => [...b, { xPct: ((e.clientX - r.left) / r.width) * 100, yPct: ((e.clientY - r.top) / r.height) * 100, text: "" }]);
  };

  const dragStart = (i) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = areaRef.current.getBoundingClientRect();
    const move = (ev) => {
      setBoxes((bs) => bs.map((b, j) => j === i ? {
        ...b,
        xPct: Math.min(92, Math.max(0, ((ev.clientX - r.left) / r.width) * 100)),
        yPct: Math.min(92, Math.max(0, ((ev.clientY - r.top) / r.height) * 100)),
      } : b));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const delAnnot = async (i) => {
    try {
      await api.delete(`/medical-plans/${planId}/annotations/${i}`);
      toast.success("Annotation removed");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAnnotated = async () => {
    setBusy(true);
    try {
      const im = new Image();
      await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = imgSrc; });
      const canvas = document.createElement("canvas");
      canvas.width = imgNat.w; canvas.height = imgNat.h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(im, 0, 0, imgNat.w, imgNat.h);
      const fontPx = Math.max(14, Math.round(imgNat.w / 45));
      for (const b of boxes) {
        if (!b.text.trim()) continue;
        const x = (b.xPct / 100) * imgNat.w;
        const y = (b.yPct / 100) * imgNat.h;
        ctx.font = `bold ${fontPx}px Arial`;
        const maxW = imgNat.w * 0.4;
        const words = b.text.split(/\s+/);
        const lines = [];
        let cur = "";
        for (const w of words) {
          const t = cur ? `${cur} ${w}` : w;
          if (ctx.measureText(t).width > maxW - fontPx && cur) { lines.push(cur); cur = w; } else cur = t;
        }
        if (cur) lines.push(cur);
        const pad = fontPx * 0.5;
        const lineH = fontPx * 1.3;
        const boxW = Math.min(maxW, Math.max(...lines.map((l) => ctx.measureText(l).width)) + pad * 2);
        const boxH = lines.length * lineH + pad * 1.5;
        ctx.fillStyle = "rgba(255,255,255,0.93)";
        ctx.strokeStyle = "#005EB8";
        ctx.lineWidth = Math.max(2, fontPx / 7);
        ctx.fillRect(x, y, boxW, boxH);
        ctx.strokeRect(x, y, boxW, boxH);
        ctx.fillStyle = "#0F2547";
        lines.forEach((l, li) => ctx.fillText(l, x + pad, y + pad + (li + 1) * lineH - lineH * 0.35));
      }
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("Couldn't export the image — try a smaller photo");
      const fd = new FormData();
      fd.append("file", new File([blob], `${(name || "annotated").replace(/\.[^.]+$/, "")}.png`, { type: "image/png" }));
      fd.append("name", name || "Annotated plan");
      await api.post(`/medical-plans/${planId}/annotations`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Annotated image saved — it attaches to the plan email automatically");
      setImgSrc(""); setBoxes([]); setName("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {(annotations || []).length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="mp-annot-list">
          {annotations.map((a, i) => (
            <div key={`${a.path}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`mp-annot-row-${i}`}>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/medical-plans/${planId}/annotations/${i}`} target="_blank" rel="noreferrer"
                className="link-chip text-xs" data-testid={`mp-annot-view-${i}`}>{a.name}</a>
              <span className="text-[10px] text-slate-400">{a.by} · {new Date(a.at).toLocaleDateString("en-GB")}</span>
              <button type="button" onClick={() => delAnnot(i)} data-testid={`mp-annot-remove-${i}`} aria-label="Remove annotated image"
                className="h-7 w-7 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 ml-auto">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input type="file" accept="image/*" onChange={pick} data-testid="mp-annot-upload"
        className="text-sm text-slate-600 file:mr-3 file:h-10 file:px-3 file:rounded-lg file:border file:border-slate-300 file:bg-white file:text-xs file:font-bold file:text-[#005EB8] file:cursor-pointer" />
      {imgSrc && (
        <>
          <div ref={areaRef} onPointerDown={addBox} data-testid="mp-annot-canvas"
            className="relative mt-3 border border-slate-300 rounded-lg overflow-hidden select-none touch-none" style={{ cursor: "crosshair" }}>
            <img src={imgSrc} alt="Plan to annotate" className="w-full block" draggable={false} />
            {boxes.map((b, i) => (
              <div key={i} data-box={i} className="absolute bg-white/95 border-2 border-[#005EB8] rounded-md shadow-md"
                style={{ left: `${b.xPct}%`, top: `${b.yPct}%`, width: "42%" }}>
                <div className="flex items-center justify-between bg-[#005EB8] text-white text-[10px] font-bold px-1.5 py-0.5 cursor-move"
                  onPointerDown={dragStart(i)}>
                  <span>Box {i + 1} — drag me</span>
                  <button type="button" onClick={() => setBoxes((bs) => bs.filter((_, j) => j !== i))} data-testid={`mp-annot-del-${i}`}
                    aria-label="Remove box" className="px-1 leading-none">×</button>
                </div>
                <textarea rows={2} value={b.text} placeholder="Type here…" data-testid={`mp-annot-text-${i}`}
                  className="w-full text-xs p-1.5 focus:outline-none resize-y rounded-b-md"
                  onPointerDown={(e) => e.stopPropagation()}
                  onChange={(e) => setBoxes((bs) => bs.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
              </div>
            ))}
          </div>
          <input className={inputCls + " mt-2"} placeholder="Name for this annotated image" value={name}
            onChange={(e) => setName(e.target.value)} data-testid="mp-annot-name" />
          <button type="button" onClick={saveAnnotated} disabled={busy || !boxes.some((b) => b.text.trim())} data-testid="mp-annot-save"
            className="mt-2 h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold disabled:opacity-50 transition-colors">
            {busy ? "Saving…" : "Save annotated image"}
          </button>
        </>
      )}
    </div>
  );
}

export default function MedicalPlan() {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [plan, setPlan] = useState(null);
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ client_id: "", event_id: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", client_id: "", event_id: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ emails: [], note: "", include_docx: true, custom: "" });
  const [hospitalOpts, setHospitalOpts] = useState(null);
  const [hospitalLoading, setHospitalLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: c }, { data: e }] = await Promise.all([
        api.get("/medical-plans"), api.get("/clients"), api.get("/events"),
      ]);
      setPlans(p); setClients(c); setEvents(e);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setAt = (k, i, sub, v) => setData((d) => {
    const arr = [...(d[k] || [])];
    arr[i] = { ...arr[i], [sub]: v };
    return { ...d, [k]: arr };
  });

  const createPlan = async () => {
    try {
      const { data: created } = await api.post("/medical-plans", createForm);
      toast.success("Medical plan started — details auto-filled where known");
      setCreateOpen(false);
      setCreateForm({ client_id: "", event_id: "" });
      openPlan(created.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPlan = async () => {
    if (!uploadFile) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("title", uploadForm.title);
      fd.append("client_id", uploadForm.client_id);
      fd.append("event_id", uploadForm.event_id);
      await api.post("/medical-plans/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Medical plan uploaded and assigned");
      setUploadOpen(false);
      setUploadForm({ title: "", client_id: "", event_id: "" });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const openPlan = async (id) => {
    try {
      const { data: p } = await api.get(`/medical-plans/${id}`);
      const keyed = (rows) => (rows || []).map((r) => ({ ...r, _k: crypto.randomUUID() }));
      setPlan(p); setTitle(p.title); setView("edit");
      setData({ ...p.data, contacts: keyed(p.data.contacts), key_times: keyed(p.data.key_times), staff_req: keyed(p.data.staff_req) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const strip = (rows) => (rows || []).map(({ _k, ...r }) => r);
      const clean = { ...data, contacts: strip(data.contacts), key_times: strip(data.key_times), staff_req: strip(data.staff_req) };
      const { data: p } = await api.put(`/medical-plans/${plan.id}`, { title, data: clean });
      setPlan(p);
      toast.success("Medical plan saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/medical-plans/${plan.id}/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(plan.title || "medical-plan").replace(/[^A-Za-z0-9 _-]/g, "")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const suggestHospital = async () => {
    const venue = data.venue || data.event_location;
    if (!venue) { toast.error("Add a venue first"); return; }
    setHospitalLoading(true);
    setHospitalOpts(null);
    try {
      const { data: res } = await api.get("/medical-plans/hospital-suggest", { params: { venue } });
      if (!res.hospitals?.length) toast.error(res.message || "No hospitals found nearby");
      else setHospitalOpts(res.hospitals);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setHospitalLoading(false);
    }
  };

  const planClient = useMemo(() => clients.find((c) => c.id === plan?.client_id), [clients, plan]);

  const openSend = () => {
    const emails = [];
    if (planClient?.email) emails.push(planClient.email);
    (planClient?.contacts || []).forEach((c) => { if (c.email && !emails.includes(c.email)) emails.push(c.email); });
    setSendForm({ emails, note: "", include_docx: true, custom: "" });
    setSendOpen(true);
  };

  const doSend = async () => {
    const emails = [...sendForm.emails];
    if (sendForm.custom.trim() && !emails.includes(sendForm.custom.trim())) emails.push(sendForm.custom.trim());
    try {
      const { data: res } = await api.post(`/medical-plans/${plan.id}/send`,
        { emails, note: sendForm.note, include_docx: sendForm.include_docx });
      toast.success(res.message);
      setSendOpen(false);
      openPlan(plan.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/medical-plans/${p.id}`);
      toast.success("Medical plan deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (view === "edit" && plan && data) {
    return (
      <div className="min-h-screen bg-[#F4F7FA]" data-testid="medical-plan-editor">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <button onClick={() => { setView("list"); load(); }} data-testid="medical-plan-editor-back"
              className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
              <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
            </button>
            <div className="flex-1 min-w-0">
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="medical-plan-title"
                className="font-heading text-lg sm:text-xl font-bold text-[#0F2547] bg-transparent border-b border-transparent hover:border-slate-300 focus:border-[#005EB8] focus:outline-none w-full" />
              <p className="text-xs text-slate-500">
                {plan.client_name || "No client"} · {plan.status === "sent" ? "Sent" : "Draft"}
                {plan.send_history?.length > 0 && ` · last sent ${new Date(plan.send_history[plan.send_history.length - 1].sent_at).toLocaleDateString("en-GB")}`}
              </p>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-3 pb-28">
          <Section title="1. Plan & cover details" defaultOpen testid="mp-sec-cover">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Event name" value={data.event_name} onChange={(v) => set("event_name", v)} testid="mp-event-name" />
              <Field label="Event date" value={data.event_date} onChange={(v) => set("event_date", v)} placeholder="e.g. 04/07/2026" testid="mp-event-date" />
              <Field label="Venue" value={data.venue} onChange={(v) => set("venue", v)} testid="mp-venue" />
              <Field label="Document prepared by" value={data.prepared_by} onChange={(v) => set("prepared_by", v)} testid="mp-prepared-by" />
              <Field label="Date of issue" value={data.date_of_issue} onChange={(v) => set("date_of_issue", v)} testid="mp-date-of-issue" />
              <Field label="Version no." value={data.version_no} onChange={(v) => set("version_no", v)} testid="mp-version" />
            </div>
          </Section>

          <Section title="2. Key contacts" testid="mp-sec-contacts">
            <div className="space-y-3">
              {data.contacts.map((c, i) => (
                <div key={c._k || i} className="grid grid-cols-3 gap-2">
                  <input className={inputCls} placeholder="Name" value={c.name} data-testid={`mp-contact-name-${i}`}
                    onChange={(e) => setAt("contacts", i, "name", e.target.value)} />
                  <input className={inputCls} placeholder="Role" value={c.role} data-testid={`mp-contact-role-${i}`}
                    onChange={(e) => setAt("contacts", i, "role", e.target.value)} />
                  <input className={inputCls} placeholder="Contact number" value={c.phone} data-testid={`mp-contact-phone-${i}`}
                    onChange={(e) => setAt("contacts", i, "phone", e.target.value)} />
                </div>
              ))}
            </div>
          </Section>

          <Section title="3. Event information" testid="mp-sec-event-info">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Organisation name" value={data.org_name} onChange={(v) => set("org_name", v)} testid="mp-org-name" />
              <Field label="Event location" value={data.event_location} onChange={(v) => set("event_location", v)} testid="mp-event-location" />
              <Field label="Event organiser" value={data.event_organiser} onChange={(v) => set("event_organiser", v)} testid="mp-event-organiser" />
              <Field label="Email address" value={data.email} onChange={(v) => set("email", v)} testid="mp-email" />
              <Field label="Contact number" value={data.contact_number} onChange={(v) => set("contact_number", v)} testid="mp-contact-number" />
              <Field label="what3words location" value={data.w3w} onChange={(v) => set("w3w", v)} placeholder="e.g. filled.count.soap" testid="mp-w3w" />
              <Field label="Event start time" value={data.start_time} onChange={(v) => set("start_time", v)} testid="mp-start-time" />
              <Field label="Event finish time" value={data.finish_time} onChange={(v) => set("finish_time", v)} testid="mp-finish-time" />
              <Field label="Expected attendance" value={data.expected_attendance} onChange={(v) => set("expected_attendance", v)} testid="mp-attendance" />
              <Field label="Audience profile" value={data.audience_profile} onChange={(v) => set("audience_profile", v)} testid="mp-audience" />
              <Field label="Event type" value={data.event_type} onChange={(v) => set("event_type", v)} testid="mp-event-type" />
            </div>
          </Section>

          <Section title="4. Event risk profile" testid="mp-sec-risk">
            <div className="grid sm:grid-cols-2 gap-3">
              <SelField label="Overall event risk level" value={data.risk_overall} onChange={(v) => set("risk_overall", v)} options={RISK_OPTS} testid="mp-risk-overall" />
              <SelField label="Medical risk level" value={data.risk_medical} onChange={(v) => set("risk_medical", v)} options={RISK_OPTS} testid="mp-risk-medical" />
              <SelField label="Crowd risk level" value={data.risk_crowd} onChange={(v) => set("risk_crowd", v)} options={RISK_OPTS} testid="mp-risk-crowd" />
              <SelField label="Weather risk level" value={data.risk_weather} onChange={(v) => set("risk_weather", v)} options={RISK_OPTS} testid="mp-risk-weather" />
              <SelField label="Site access risk level" value={data.risk_site} onChange={(v) => set("risk_site", v)} options={RISK_OPTS} testid="mp-risk-site" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <SelField label="Fireworks / pyrotechnics" value={data.fireworks} onChange={(v) => set("fireworks", v)} options={YN_OPTS} testid="mp-fireworks" />
              <SelField label="Alcohol present" value={data.alcohol} onChange={(v) => set("alcohol", v)} options={YN_OPTS} testid="mp-alcohol" />
              <SelField label="Children expected" value={data.children} onChange={(v) => set("children", v)} options={YN_OPTS} testid="mp-children" />
              <SelField label="Vulnerable persons expected" value={data.vulnerable} onChange={(v) => set("vulnerable", v)} options={YN_OPTS} testid="mp-vulnerable" />
              <SelField label="Emergency vehicle access confirmed" value={data.veh_access} onChange={(v) => set("veh_access", v)} options={YN_OPTS} testid="mp-veh-access" />
            </div>
          </Section>

          <Section title="5. Facilities & operational arrangements" testid="mp-sec-ops">
            <div className="space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <CheckField label="Medical gazebo" checked={data.gazebo} onChange={(v) => set("gazebo", v)} testid="mp-gazebo" />
                {data.gazebo && <input className={inputCls + " !w-32"} placeholder="Size e.g. 6x3" value={data.gazebo_size} data-testid="mp-gazebo-size" onChange={(e) => set("gazebo_size", e.target.value)} />}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <CheckField label="Medical marquee" checked={data.marquee} onChange={(v) => set("marquee", v)} testid="mp-marquee" />
                {data.marquee && <input className={inputCls + " !w-32"} placeholder="Size e.g. 9x6" value={data.marquee_size} data-testid="mp-marquee-size" onChange={(e) => set("marquee_size", e.target.value)} />}
              </div>
              <CheckField label="Permanent medical facility" checked={data.permanent_facility} onChange={(v) => set("permanent_facility", v)} testid="mp-permanent" />
            </div>
          </Section>

          <Section title="6. Key event times" testid="mp-sec-key-times">
            <Field label="Event date" value={data.key_event_date} onChange={(v) => set("key_event_date", v)} testid="mp-key-event-date" />
            <div className="space-y-2 mt-3">
              {data.key_times.map((row, i) => (
                <div key={row._k || i} className="grid grid-cols-[1fr_110px] gap-2">
                  <input className={inputCls} placeholder={`Activity ${i + 1}`} value={row.activity} data-testid={`mp-activity-${i}`}
                    onChange={(e) => setAt("key_times", i, "activity", e.target.value)} />
                  <input className={inputCls} placeholder="Time" value={row.time} data-testid={`mp-time-${i}`}
                    onChange={(e) => setAt("key_times", i, "time", e.target.value)} />
                </div>
              ))}
            </div>
          </Section>

          <Section title="7. Parking, passes & treatment facilities" testid="mp-sec-parking">
            <div className="grid gap-3">
              <Field label="Parking arrangements" value={data.parking} onChange={(v) => set("parking", v)} testid="mp-parking" />
              <Field label="Medical vehicle location" value={data.vehicle_location} onChange={(v) => set("vehicle_location", v)} testid="mp-vehicle-location" />
              <Field label="Emergency vehicle access route" value={data.access_route} onChange={(v) => set("access_route", v)} testid="mp-access-route" />
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">Security passes & accreditation</p>
            <CheckField label="Staff accreditation required" checked={data.pass_staff} onChange={(v) => set("pass_staff", v)} testid="mp-pass-staff" />
            <CheckField label="Vehicle pass required" checked={data.pass_vehicle} onChange={(v) => set("pass_vehicle", v)} testid="mp-pass-vehicle" />
            <CheckField label="Wristband required" checked={data.pass_wristband} onChange={(v) => set("pass_wristband", v)} testid="mp-pass-wristband" />
            <CheckField label="Backstage access required" checked={data.pass_backstage} onChange={(v) => set("pass_backstage", v)} testid="mp-pass-backstage" />
            <CheckField label="No special accreditation required" checked={data.pass_none} onChange={(v) => set("pass_none", v)} testid="mp-pass-none" />
            <Field label="Additional information" value={data.pass_info} onChange={(v) => set("pass_info", v)} testid="mp-pass-info" />
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">Treatment facilities</p>
            <div className="grid sm:grid-cols-2 gap-x-4">
              <CheckField label="Medical gazebo" checked={data.fac_gazebo} onChange={(v) => set("fac_gazebo", v)} testid="mp-fac-gazebo" />
              <CheckField label="Medical marquee" checked={data.fac_marquee} onChange={(v) => set("fac_marquee", v)} testid="mp-fac-marquee" />
              <CheckField label="Permanent medical facility" checked={data.fac_permanent} onChange={(v) => set("fac_permanent", v)} testid="mp-fac-permanent" />
              <CheckField label="Treatment bay area" checked={data.fac_bay} onChange={(v) => set("fac_bay", v)} testid="mp-fac-bay" />
              <CheckField label="Waiting area" checked={data.fac_waiting} onChange={(v) => set("fac_waiting", v)} testid="mp-fac-waiting" />
              <CheckField label="Lost child area" checked={data.fac_lost_child} onChange={(v) => set("fac_lost_child", v)} testid="mp-fac-lost-child" />
              <CheckField label="Sensory area" checked={data.fac_sensory} onChange={(v) => set("fac_sensory", v)} testid="mp-fac-sensory" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <Field label="Facility size" value={data.facility_size} onChange={(v) => set("facility_size", v)} testid="mp-facility-size" />
              <Field label="Additional notes" value={data.facility_notes} onChange={(v) => set("facility_notes", v)} testid="mp-facility-notes" />
            </div>
          </Section>

          <Section title="8. Receiving hospital & staff requirements" testid="mp-sec-hospital">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <p className="text-xs text-slate-500">Auto-suggested from the venue — confirm or override.</p>
              <button type="button" onClick={suggestHospital} disabled={hospitalLoading} data-testid="mp-hospital-suggest"
                className="h-10 px-3 rounded-lg bg-[#005EB8] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                <Hospital className="h-4 w-4" /> {hospitalLoading ? "Searching…" : "Suggest nearest A&E"}
              </button>
            </div>
            {hospitalOpts && (
              <div className="mb-3 rounded-lg border border-slate-200 divide-y" data-testid="mp-hospital-options">
                {hospitalOpts.map((h, i) => (
                  <button key={`${h.name}-${h.postcode || h.address || ""}`} type="button" data-testid={`mp-hospital-option-${i}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => { setData((d) => ({ ...d, hospital_name: h.name, hospital_address: h.address, hospital_postcode: h.postcode })); setHospitalOpts(null); }}>
                    <span className="font-semibold text-[#0F2547]">{h.name}</span>
                    <span className="block text-xs text-slate-500">{h.address}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Hospital name" value={data.hospital_name} onChange={(v) => set("hospital_name", v)} testid="mp-hospital-name" />
              <Field label="Address" value={data.hospital_address} onChange={(v) => set("hospital_address", v)} testid="mp-hospital-address" />
              <Field label="Postcode" value={data.hospital_postcode} onChange={(v) => set("hospital_postcode", v)} testid="mp-hospital-postcode" />
              <Field label="Telephone number" value={data.hospital_phone} onChange={(v) => set("hospital_phone", v)} testid="mp-hospital-phone" />
              <Field label="Access door code" value={data.door_code} onChange={(v) => set("door_code", v)} testid="mp-door-code" />
              <Field label="Additional notes" value={data.hospital_notes} onChange={(v) => set("hospital_notes", v)} testid="mp-hospital-notes" />
            </div>
            <CheckField label="This hospital has an Emergency Department (A&E)" checked={data.has_ed} onChange={(v) => set("has_ed", v)} testid="mp-has-ed" />
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">Staff requirements</p>
            <div className="space-y-2">
              {data.staff_req.map((row, i) => (
                <div key={row._k || i} className="grid grid-cols-[70px_1fr] gap-2">
                  <input className={inputCls} placeholder="Qty" value={row.qty} data-testid={`mp-staff-qty-${i}`}
                    onChange={(e) => setAt("staff_req", i, "qty", e.target.value)} />
                  <input className={inputCls} placeholder="Qualification / role" value={row.role} data-testid={`mp-staff-role-${i}`}
                    onChange={(e) => setAt("staff_req", i, "role", e.target.value)} />
                </div>
              ))}
            </div>
          </Section>

          <Section title="9. Major incident — additional resources" testid="mp-sec-resources">
            <CheckField label="Ambulance Support Units" checked={data.res_ambulance} onChange={(v) => set("res_ambulance", v)} testid="mp-res-ambulance" />
            <CheckField label="Additional Medical Staff" checked={data.res_staff} onChange={(v) => set("res_staff", v)} testid="mp-res-staff" />
            <CheckField label="Specialist Response Teams" checked={data.res_specialist} onChange={(v) => set("res_specialist", v)} testid="mp-res-specialist" />
            <CheckField label="Welfare Support Teams" checked={data.res_welfare} onChange={(v) => set("res_welfare", v)} testid="mp-res-welfare" />
            <CheckField label="Emergency Service Assets" checked={data.res_assets} onChange={(v) => set("res_assets", v)} testid="mp-res-assets" />
          </Section>

          <Section title="10. Medical centre layout & resources" testid="mp-sec-mc">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Medical facility type</p>
            <div className="flex gap-4 flex-wrap">
              <CheckField label="Gazebo" checked={data.mc_gazebo} onChange={(v) => set("mc_gazebo", v)} testid="mp-mc-gazebo" />
              <CheckField label="Marquee" checked={data.mc_marquee} onChange={(v) => set("mc_marquee", v)} testid="mp-mc-marquee" />
              <CheckField label="Permanent facility" checked={data.mc_permanent} onChange={(v) => set("mc_permanent", v)} testid="mp-mc-permanent" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
              <Field label="Size — length (m)" value={data.mc_size_l} onChange={(v) => set("mc_size_l", v)} testid="mp-mc-size-l" />
              <Field label="Size — width (m)" value={data.mc_size_w} onChange={(v) => set("mc_size_w", v)} testid="mp-mc-size-w" />
              <Field label="Treatment bays" value={data.mc_bays} onChange={(v) => set("mc_bays", v)} testid="mp-mc-bays" />
              <SelField label="Waiting area" value={data.mc_waiting} onChange={(v) => set("mc_waiting", v)} options={YN_OPTS} testid="mp-mc-waiting" />
              <SelField label="Sensory area" value={data.mc_sensory} onChange={(v) => set("mc_sensory", v)} options={YN_OPTS} testid="mp-mc-sensory" />
              <Field label="Treatment bays available" value={data.mc_bays_available} onChange={(v) => set("mc_bays_available", v)} testid="mp-mc-bays-available" />
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">Clinical equipment</p>
            <div className="grid sm:grid-cols-2 gap-x-4">
              <CheckField label="AED units" checked={data.eq_aed} onChange={(v) => set("eq_aed", v)} testid="mp-eq-aed" />
              <CheckField label="Monitor / Defibrillator" checked={data.eq_monitor} onChange={(v) => set("eq_monitor", v)} testid="mp-eq-monitor" />
              <CheckField label="Crash trolley" checked={data.eq_crash} onChange={(v) => set("eq_crash", v)} testid="mp-eq-crash" />
              <CheckField label="Trauma bags" checked={data.eq_trauma} onChange={(v) => set("eq_trauma", v)} testid="mp-eq-trauma" />
              <CheckField label="Medical trolleys" checked={data.eq_trolleys} onChange={(v) => set("eq_trolleys", v)} testid="mp-eq-trolleys" />
              <CheckField label="Treatment couches" checked={data.eq_couches} onChange={(v) => set("eq_couches", v)} testid="mp-eq-couches" />
              <CheckField label="Oxygen equipment" checked={data.eq_oxygen} onChange={(v) => set("eq_oxygen", v)} testid="mp-eq-oxygen" />
              <CheckField label="Suction equipment" checked={data.eq_suction} onChange={(v) => set("eq_suction", v)} testid="mp-eq-suction" />
              <CheckField label="Additional specialist equipment" checked={data.eq_specialist} onChange={(v) => set("eq_specialist", v)} testid="mp-eq-specialist" />
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">Ambulance resources</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <SelField label="Ambulance on site" value={data.amb_on_site} onChange={(v) => set("amb_on_site", v)} options={YN_OPTS} testid="mp-amb-on-site" />
              <Field label="Dedicated crew" value={data.amb_crew} onChange={(v) => set("amb_crew", v)} testid="mp-amb-crew" />
              <Field label="Response capability" value={data.amb_response} onChange={(v) => set("amb_response", v)} testid="mp-amb-response" />
              <Field label="Additional vehicles" value={data.amb_vehicles} onChange={(v) => set("amb_vehicles", v)} testid="mp-amb-vehicles" />
              <Field label="Equipment carried" value={data.amb_equipment} onChange={(v) => set("amb_equipment", v)} testid="mp-amb-equipment" />
            </div>
          </Section>

          <Section title="11. Medical equipment & asset register" testid="mp-sec-register">
            <p className="text-xs text-slate-500 mb-3">Quantities are pre-set to standard load-out — adjust as needed.</p>
            {EQUIPMENT_SECTIONS.map((sec) => (
              <div key={sec.key} className="mb-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{sec.title}</p>
                <div className="space-y-1">
                  {sec.items.map((item, i) => (
                    <div key={item} className="grid grid-cols-[1fr_80px] gap-2 items-center">
                      <span className="text-sm text-slate-700">{item}</span>
                      <input className={inputCls + " !h-9 text-center"} value={(data[sec.key] || [])[i] || ""}
                        data-testid={`mp-${sec.key}-${i}`}
                        onChange={(e) => setData((d) => { const arr = [...(d[sec.key] || [])]; arr[i] = e.target.value; return { ...d, [sec.key]: arr }; })} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Section>

          <Section title="12. Event organiser requirements & site support" testid="mp-sec-support">
            <div className="space-y-2">
              {SUPPORT_ITEMS.map(([k, label]) => (
                <div key={k} className="grid grid-cols-[1fr_170px] gap-2 items-center">
                  <span className="text-sm text-slate-700">{label}</span>
                  <select className={selectCls + " !h-9"} value={data.support?.[k] || "preferred"} data-testid={`mp-support-${k}`}
                    onChange={(e) => setData((d) => ({ ...d, support: { ...(d.support || {}), [k]: e.target.value } }))}>
                    {SUPPORT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </Section>

          <Section title="13. Event Risk Identification Document (ERID)" testid="mp-sec-erid">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Prepared by" value={data.erid_prepared_by} onChange={(v) => set("erid_prepared_by", v)} testid="mp-erid-prepared-by" />
              <Field label="Date prepared" value={data.erid_date_prepared} onChange={(v) => set("erid_date_prepared", v)} testid="mp-erid-date-prepared" />
              <Field label="Review date" value={data.erid_review_date} onChange={(v) => set("erid_review_date", v)} testid="mp-erid-review-date" />
            </div>
          </Section>

          <Section title="14. Extra notes & custom sections" testid="mp-sec-custom">
            <p className="text-xs text-slate-500 mb-3">Anything else you want on the plan — each box appears as its own labelled section on the PDF/Word when sent.</p>
            <div className="space-y-3">
              {(data.custom_sections || []).map((c, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`mp-custom-${i}`}>
                  <div className="flex gap-2">
                    <input className={inputCls} placeholder="Section label (e.g. Site layout notes)" value={c.label}
                      data-testid={`mp-custom-label-${i}`}
                      onChange={(e) => setData((d) => { const arr = [...(d.custom_sections || [])]; arr[i] = { ...arr[i], label: e.target.value }; return { ...d, custom_sections: arr }; })} />
                    <button type="button" onClick={() => setData((d) => ({ ...d, custom_sections: (d.custom_sections || []).filter((_, j) => j !== i) }))}
                      data-testid={`mp-custom-del-${i}`} aria-label="Remove section"
                      className="h-11 w-11 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                    placeholder="Type the text for this box…" value={c.text} data-testid={`mp-custom-text-${i}`}
                    onChange={(e) => setData((d) => { const arr = [...(d.custom_sections || [])]; arr[i] = { ...arr[i], text: e.target.value }; return { ...d, custom_sections: arr }; })} />
                </div>
              ))}
              <button type="button" onClick={() => setData((d) => ({ ...d, custom_sections: [...(d.custom_sections || []), { label: "", text: "" }] }))}
                data-testid="mp-custom-add"
                className="h-10 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add a text box section
              </button>
            </div>
          </Section>

          <Section title="15. Annotated site plan / layout (optional)" testid="mp-sec-annotate">
            <p className="text-xs text-slate-500 mb-3">
              Upload a photo or scan (site map, hand-drawn layout). Tap anywhere on it to drop a text box, type your note, drag it into place.
              Saved images attach to the plan email automatically.
            </p>
            <AnnotationEditor planId={plan.id} annotations={plan.annotations || []} onChanged={() => openPlan(plan.id)} />
          </Section>

          {plan.send_history?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mp-send-history">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Send history</p>
              <div className="space-y-1">
                {plan.send_history.map((h, i) => (
                  <p key={`${h.sent_at}-${(h.recipients || []).join(",")}`} className="text-xs text-slate-600" data-testid={`mp-send-entry-${i}`}>
                    {new Date(h.sent_at).toLocaleString("en-GB")} — emailed to {h.recipients.join(", ")} by {h.by}
                    {h.include_docx ? " (PDF + Word)" : " (PDF)"}
                  </p>
                ))}
              </div>
            </div>
          )}
        </main>

        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-20 pointer-events-none">
          <div className="max-w-3xl mx-auto px-4 py-3 flex gap-2 pointer-events-none">
            <button onClick={save} disabled={saving} data-testid="mp-save"
              className="flex-1 h-12 rounded-xl bg-[#0F2547] text-white text-sm font-bold disabled:opacity-50 pointer-events-auto">
              {saving ? "Saving…" : "Save plan"}
            </button>
            <button onClick={() => download("pdf")} data-testid="mp-download-pdf"
              className="h-12 px-4 rounded-xl border border-slate-300 text-sm font-bold text-[#0F2547] flex items-center gap-1.5 pointer-events-auto">
              <FileDown className="h-4 w-4" /> PDF
            </button>
            <button onClick={() => download("docx")} data-testid="mp-download-docx"
              className="h-12 px-4 rounded-xl border border-slate-300 text-sm font-bold text-[#0F2547] flex items-center gap-1.5 pointer-events-auto">
              <FileText className="h-4 w-4" /> Word
            </button>
            <button onClick={openSend} data-testid="mp-send"
              className="h-12 px-4 rounded-xl bg-[#005EB8] text-white text-sm font-bold flex items-center gap-1.5 pointer-events-auto">
              <Send className="h-4 w-4" /> Send
            </button>
          </div>
        </div>

        <Dialog open={sendOpen} onOpenChange={setSendOpen}>
          <DialogContent className="max-w-md" data-testid="mp-send-dialog">
            <DialogHeader><DialogTitle>Send medical plan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {(() => {
                const opts = [];
                if (planClient?.email) opts.push({ email: planClient.email, label: `${planClient.name} (main)` });
                (planClient?.contacts || []).forEach((c) => { if (c.email) opts.push({ email: c.email, label: `${c.name}${c.role ? ` — ${c.role}` : ""}` }); });
                if (!opts.length) return <p className="text-sm text-slate-500">This client has no email addresses saved — add one below.</p>;
                return opts.map((o) => (
                  <label key={o.email} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 accent-[#005EB8]" data-testid={`mp-send-to-${o.email}`}
                      checked={sendForm.emails.includes(o.email)}
                      onChange={(e) => setSendForm((f) => ({ ...f, emails: e.target.checked ? [...f.emails, o.email] : f.emails.filter((x) => x !== o.email) }))} />
                    <span className="text-sm text-slate-700">{o.label} <span className="text-slate-400">({o.email})</span></span>
                  </label>
                ));
              })()}
              <input className={inputCls} placeholder="Another email address (optional)" value={sendForm.custom}
                data-testid="mp-send-custom" onChange={(e) => setSendForm((f) => ({ ...f, custom: e.target.value }))} />
              <textarea className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" rows={3}
                placeholder="Note to include in the email (optional)" value={sendForm.note} data-testid="mp-send-note"
                onChange={(e) => setSendForm((f) => ({ ...f, note: e.target.value }))} />
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-[#005EB8]" checked={sendForm.include_docx}
                  data-testid="mp-send-include-docx" onChange={(e) => setSendForm((f) => ({ ...f, include_docx: e.target.checked }))} />
                <span className="text-sm text-slate-700">Also attach editable Word version</span>
              </label>
              <button onClick={doSend} data-testid="mp-send-confirm"
                className="w-full h-12 rounded-xl bg-[#005EB8] text-white text-sm font-bold">Email plan</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="medical-plan-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="medical-plan-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">Medical Plan</h1>
            <p className="text-xs text-slate-500">Build & send event medical plans</p>
          </div>
          <button onClick={() => setUploadOpen(true)} data-testid="mp-upload-plan"
            className="h-11 px-4 rounded-xl border-2 border-[#005EB8] text-[#005EB8] bg-white text-sm font-bold flex items-center gap-1.5">
            <Upload className="h-4 w-4" /> Upload plan
          </button>
          <button onClick={() => setCreateOpen(true)} data-testid="mp-new-plan"
            className="h-11 px-4 rounded-xl bg-[#005EB8] text-white text-sm font-bold flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> New plan
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-3">
        {plans.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="mp-empty">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h2 className="font-bold text-lg text-[#0F2547] mb-2">No medical plans yet</h2>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Pick a client and event — the plan auto-fills with their details, the venue, your roster and your name.
              Then fill the rest, download as PDF/Word, and email it to the client.
            </p>
          </div>
        )}
        {plans.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`mp-plan-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#0F2547] text-sm truncate">{p.title}</p>
              <p className="text-xs text-slate-500">
                {p.client_name || "No client"} · by {p.created_by_name}
                {p.uploaded_file && ` · uploaded file: ${p.uploaded_file.name}`}
                {p.send_history?.length > 0
                  ? ` · sent ${new Date(p.send_history[p.send_history.length - 1].sent_at).toLocaleDateString("en-GB")} to ${p.send_history[p.send_history.length - 1].recipients.length} contact(s)`
                  : " · not sent yet"}
              </p>
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${p.status === "sent" ? "bg-emerald-50 text-[#007F3B]" : "bg-slate-100 text-slate-600"}`}
              data-testid={`mp-status-${p.id}`}>
              {p.status === "sent" ? "Sent" : "Draft"}
            </span>
            {p.uploaded_file && (
              <button onClick={async () => {
                  try {
                    const res = await api.get(`/medical-plans/${p.id}/file`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url; a.download = p.uploaded_file.name || "medical-plan"; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error(formatApiError(err)); }
                }} title={`Download uploaded file (${p.uploaded_file.name})`} data-testid={`mp-file-${p.id}`}
                className="h-10 w-10 grid place-items-center rounded-lg hover:bg-emerald-50 text-[#007F3B]">
                <Download className="h-4 w-4" />
              </button>
            )}
            <button onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/medical-plans/${p.id}/portal-share`, { shared: !p.portal_shared });
                  toast.success(data.message);
                  setPlans((ps) => ps.map((x) => (x.id === p.id ? { ...x, portal_shared: !p.portal_shared } : x)));
                } catch (err) { toast.error(formatApiError(err)); }
              }} title={p.portal_shared ? "Shared to client portal — click to remove" : "Share to client portal"}
              data-testid={`mp-share-${p.id}`}
              className={`h-10 w-10 grid place-items-center rounded-lg hover:bg-slate-100 ${p.portal_shared ? "text-[#007F3B]" : "text-slate-400"}`}>
              <Share2 className="h-4 w-4" />
            </button>
            <button onClick={() => openPlan(p.id)} data-testid={`mp-edit-${p.id}`}
              className="h-10 w-10 grid place-items-center rounded-lg hover:bg-slate-100">
              <Pencil className="h-4 w-4 text-[#005EB8]" />
            </button>
            <button onClick={() => remove(p)} data-testid={`mp-delete-${p.id}`}
              className="h-10 w-10 grid place-items-center rounded-lg hover:bg-red-50">
              <Trash2 className="h-4 w-4 text-[#D5281B]" />
            </button>
          </div>
        ))}
      </main>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="mp-upload-dialog">
          <DialogHeader><DialogTitle>Upload a medical plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Plan file (PDF, Word or image) *</span>
              <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" data-testid="mp-upload-file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-600 file:mr-3 file:h-10 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-sm file:font-bold file:text-[#0F2547] hover:file:bg-slate-200" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Title (optional — file name used if blank)</span>
              <input className={selectCls} value={uploadForm.title} data-testid="mp-upload-title" placeholder="e.g. Medical plan — Summer Festival 2026"
                onChange={(e) => setUploadForm((f) => ({ ...f, title: e.target.value }))} />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Assign to client / organiser (optional)</span>
              <select className={selectCls} value={uploadForm.client_id} data-testid="mp-upload-client"
                onChange={(e) => setUploadForm((f) => ({ ...f, client_id: e.target.value }))}>
                <option value="">— No client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Assign to event (optional)</span>
              <select className={selectCls} value={uploadForm.event_id} data-testid="mp-upload-event"
                onChange={(e) => setUploadForm((f) => ({ ...f, event_id: e.target.value }))}>
                <option value="">— No event —</option>
                {events.map((e) => <option key={e.id} value={e.id}>{e.name}{e.event_date ? ` — ${e.event_date}` : ""}</option>)}
              </select>
            </label>
            <p className="text-xs text-slate-500">
              The event and organiser details are auto-filled from your calendar, and the file stays attached to the plan for downloading or sharing to the client portal.
            </p>
            <button onClick={uploadPlan} disabled={uploading} data-testid="mp-upload-confirm"
              className="w-full h-12 rounded-xl bg-[#005EB8] text-white text-sm font-bold disabled:opacity-60">
              {uploading ? "Uploading…" : "Upload plan"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="mp-create-dialog">
          <DialogHeader><DialogTitle>New medical plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Client (optional)</span>
              <select className={selectCls} value={createForm.client_id} data-testid="mp-create-client"
                onChange={(e) => setCreateForm((f) => ({ ...f, client_id: e.target.value }))}>
                <option value="">— No client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Event (optional)</span>
              <select className={selectCls} value={createForm.event_id} data-testid="mp-create-event"
                onChange={(e) => setCreateForm((f) => ({ ...f, event_id: e.target.value }))}>
                <option value="">— No event —</option>
                {events.map((e) => <option key={e.id} value={e.id}>{e.name}{e.event_date ? ` — ${e.event_date}` : ""}</option>)}
              </select>
            </label>
            <p className="text-xs text-slate-500">
              Client, event, roster and your own details are auto-filled. You can edit everything afterwards.
            </p>
            <button onClick={createPlan} data-testid="mp-create-confirm"
              className="w-full h-12 rounded-xl bg-[#005EB8] text-white text-sm font-bold">Start plan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
